import React from 'react';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { formatDateTime } from '../../../../utils/functions';
import CommonTable from '../../../global/table';
import { Typography, Row, Col, Tag } from 'antd';
import {
    UserOutlined,
    MailOutlined,
    InfoCircleOutlined,
    CalendarOutlined,
    MessageOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import CommonCard from '../../../global/card';

const { Text } = Typography;

const EnhancedAutoresponseSentDetails = ({ id }) => {

    // Fetch details
    const { data: detailData, isLoading } = useGenericQuery({
        endpoint: `reports/autoresponder/sent/list?id=${id}`,
        queryKey: ["autoresponse-sent-detail", id],
        enabled: !!id
    });

    // Debugging: Log the incoming data to help verify structure
    console.log("EnhancedAutoresponseSentDetails detailData:", detailData);

    // Handle various data structures
    let result = null;
    if (Array.isArray(detailData)) {
        result = detailData[0];
    } else if (Array.isArray(detailData?.results)) {
        result = detailData.results[0];
    } else if (Array.isArray(detailData?.data?.results)) {
        result = detailData.data.results[0];
    } else {
        result = detailData?.results || detailData; // final fallback
    }

    // Extract main parts with case-insensitive fallback
    const autoresponder = result?.Autoresponder?.[0] || result?.autoresponder?.[0] || {};
    // Try 'leads' first, then 'leadSent'. Ensure it's an array.
    const leads = result?.leads || result?.leadSent || [];
    console.log("leads", leads)


    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-20 !min-h-[600px]">
                <div className="relative size-12 mb-4">
                    <div className="absolute inset-0 rounded-full border-4 border-primary/10" />
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-primary animate-spin" />
                </div>
                <Text type="secondary" className="animate-pulse">Loading autoresponse sent details...</Text>
            </div>
        );
    }

    if (!result) {
        return <div className="text-center p-6 text-gray-500">No details found.</div>;
    }


    // Sections helpers
    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" } })}
            <span style={{ fontSize: "clamp(13px, 4vw, 15px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const InfoItem = ({ label, value, icon }) => (
        <Col xs={24} sm={12}>
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                    <span style={{ color: "var(--primary-color, #1890ff)" }}>{icon}</span>
                    {label}
                </span>
                <span className="text-sm font-semibold text-gray-900">{value || "—"}</span>
            </div>
        </Col>
    );

    // Map leads for the table
    const leadsData = leads.map((item, index) => {
        const detail = item.leadDetail || item;

        return {
            key: item.id || item._id || index,
            sNo: index + 1,
            sentAt: item.sentAt || "-",
            // Check detail for fields, fallback to email if name is empty
            name: [detail.firstName, detail.lastName].filter(Boolean).join(" ") || detail.email || "-",
            email: detail.email || "-",
            phone: detail.phone || "-",
            leadSource: detail.leadSource || "-"
        };
    });

    const columns = [
        {
            title: "S#",
            dataIndex: "sNo",
            key: "sNo",
            width: "50px",
        },
        {
            title: "Sent Time",
            dataIndex: "sentAt",
            key: "sentAt",
            render: (text) => formatDateTime(text, "h:mm") || "-"
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
        },
        {
            title: "Email",
            dataIndex: "email",
            key: "email",
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
        },
        {
            title: "Lead Source",
            dataIndex: "leadSource",
            key: "leadSource",
            render: (text) => <span className="capitalize">{text}</span>
        }
    ];

    return (
        <div className="sm:p-6 p-2 w-full min-h-full overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
            >
                <Row gutter={[24, 0]}>
                    {/* Autoresponder Information Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <SectionHeader icon={<MessageOutlined />} title="Message Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Sender Name"
                                value={autoresponder.senderName}
                                icon={<UserOutlined />}
                            />
                            <InfoItem
                                label="Sender Email"
                                value={autoresponder.senderEmail}
                                icon={<MailOutlined />}
                            />
                            <InfoItem
                                label="Autoresponder Name"
                                value={autoresponder.name || autoresponder.title}
                                icon={<InfoCircleOutlined />}
                            />
                            <InfoItem
                                label="Send Date"
                                value={formatDateTime(result.dateAsPerUserTimezone || result.processDate)}
                                icon={<CalendarOutlined />}
                            />
                        </Row>
                    </Col>

                    {/* Autoresponder Message Section */}
                    <Col span={24} className="py-4 border-b border-gray-200">
                        <SectionHeader icon={<ClockCircleOutlined />} title="Autoresponder Message" />
                        <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-100 italic shadow-sm">
                            <div
                                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: autoresponder.message }}
                            />
                        </div>
                    </Col>

                    {/* Leads Table Section */}
                    <Col span={24} className="pt-4">
                        <SectionHeader icon={<UserOutlined />} title="Autoresponder Sent Lead(s)" />
                        <div className="overflow-x-auto">
                            <CommonTable
                                columns={columns}
                                data={leadsData}
                                pagination={false}
                                className="!border-none"
                            />
                        </div>
                    </Col>
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedAutoresponseSentDetails;
